// 録音セッションのオーケストレータ。
// 従来は isRecording フラグを3つのフックがそれぞれ監視して競争していたが、
// ここで「ストリーム → MediaRecorder → 波形アナライザ → 音声認識」を
// 決定的な順序で起動・停止する。isRecording は結果の通知にのみ使う。
import { useAppStore } from "../store/appStore";
import { generateLocalTimestamp, getSessionId } from "../utils/sessionUtils";
import { audioStreamService } from "./audioStreamService";
import { convertWebmToMp3 } from "./mp3Encoder";
import { SpeechRecognitionController } from "./speechRecognitionController";

// 停止時、recognition.stop() の後に遅延して届く最終認識結果を待つ時間。
// これを待たずに保存すると最後の一文が録音データに含まれない。
const RECOGNITION_FLUSH_MS = 500;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type RecordingSessionState = "idle" | "starting" | "recording" | "stopping";

export interface RecordingStartOptions {
	language: string;
	deviceId?: string;
}

class RecordingSession {
	private state: RecordingSessionState = "idle";
	private mediaRecorder: MediaRecorder | null = null;
	private chunks: Blob[] = [];
	private startTime: number | null = null;
	private audioContext: AudioContext | null = null;
	private analyser: AnalyserNode | null = null;

	/** 音声認識コントローラ（セッションが所有し、結果はストアに流す） */
	readonly recognition = new SpeechRecognitionController({
		onFinalText: (text) => useAppStore.getState().appendTranscriptionText(text),
		onInterimText: (text) => useAppStore.getState().setInterimText(text),
		onListeningChange: (listening) => useAppStore.getState().setIsListening(listening),
		onError: (message) => useAppStore.getState().setRecognitionError(message),
	});

	getSessionState(): RecordingSessionState {
		return this.state;
	}

	/** 波形描画用のアナライザ（録音中のみ非 null） */
	getAnalyser(): AnalyserNode | null {
		return this.analyser;
	}

	/**
	 * 録音セッションを開始する。
	 * 前提: マイク許可は取得済み（audioStreamService.ensurePermission をユーザージェスチャから呼ぶこと）。
	 */
	async start(options: RecordingStartOptions): Promise<void> {
		if (this.state !== "idle") return;
		this.state = "starting";
		const store = useAppStore.getState();

		try {
			// 1. 共有ストリームを開く（波形・認識も同じストリームを参照する）
			const stream = await audioStreamService.openStream(options.deviceId);

			// 2. MediaRecorder を作成
			// QUIRK: MIMEタイプのネゴシエーション（Safari はどちらも非対応のため失敗する）
			const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
				? "audio/webm;codecs=opus"
				: "audio/webm";

			this.chunks = [];
			const mediaRecorder = new MediaRecorder(stream, { mimeType });
			this.mediaRecorder = mediaRecorder;

			mediaRecorder.ondataavailable = (e) => {
				if (e.data.size > 0) {
					this.chunks.push(e.data);
				}
			};
			mediaRecorder.onerror = (event) => {
				console.error("MediaRecorderエラーイベント:", event);
			};

			// 3. 波形描画用のアナライザを作成（AudioWaveform が参照する）
			this.audioContext = new AudioContext();
			const source = this.audioContext.createMediaStreamSource(stream);
			this.analyser = this.audioContext.createAnalyser();
			this.analyser.fftSize = 256;
			source.connect(this.analyser);
			// 音声フィードバックを避けるため destination には接続しない

			// 4. 新しい録音のために文字起こしをリセットして録音開始
			store.resetTranscriptionText();
			this.startTime = Date.now();
			mediaRecorder.start();

			// 5. 音声認識を開始（ストリーム・許可が確立済みなので追加の遅延は不要）
			this.recognition.start({ language: options.language, deviceId: options.deviceId });

			this.state = "recording";
			store.setIsRecording(true);
		} catch (error) {
			this.cleanup();
			this.state = "idle";
			store.setIsRecording(false);
			throw error;
		}
	}

	/**
	 * 録音セッションを停止し、完全な文字起こしと共に保存する。
	 */
	async stop(): Promise<void> {
		if (this.state !== "recording") return;
		this.state = "stopping";
		const store = useAppStore.getState();
		store.setIsRecording(false);

		// 録音時間は停止要求時点で確定させる（フラッシュ待ちを含めない）
		const durationSec = this.startTime ? Math.round((Date.now() - this.startTime) / 1000) : 0;

		try {
			// 1. 認識に停止を要求し、遅延して届く最終認識結果を待ってから完全停止
			this.recognition.requestStop();
			await delay(RECOGNITION_FLUSH_MS);
			this.recognition.stop();
			// QUIRK: 録音開始時刻（タイムスタンプ基準）は録音停止時にのみリセットする
			this.recognition.segmenter.resetRecordingStart();

			// 2. レコーダーを停止して onstop（全チャンク確定）を待つ
			const blob = await this.stopRecorder();

			// 3. この時点の文字起こしは遅延 final も含めて完全（保存する）
			if (blob) {
				this.saveRecording(blob, durationSec);
			}
		} finally {
			this.cleanup();
			this.state = "idle";
		}
	}

	private stopRecorder(): Promise<Blob | null> {
		return new Promise((resolve) => {
			const recorder = this.mediaRecorder;
			if (!recorder || recorder.state !== "recording") {
				resolve(null);
				return;
			}

			recorder.onstop = () => {
				resolve(new Blob(this.chunks, { type: "audio/webm" }));
			};

			try {
				recorder.stop();
			} catch (error) {
				console.error("録音停止エラー:", error);
				resolve(null);
			}
		});
	}

	/** WebM を履歴に保存し、バックグラウンドで MP3 変換する */
	private saveRecording(blob: Blob, durationSec: number): void {
		try {
			const timestamp = generateLocalTimestamp();
			const webmFile = new File([blob], `recording_${timestamp}.webm`, {
				type: "audio/webm",
			});

			const store = useAppStore.getState();
			const id = store.addRecording({
				file: webmFile,
				timestamp,
				duration: durationSec,
				transcriptionText: store.transcriptionText,
				isConverting: true, // MP3変換中フラグをセット
				sessionId: getSessionId(),
				createdAt: Date.now(),
			});

			// WebM → MP3変換（バックグラウンドで実行）
			convertWebmToMp3(blob)
				.then((mp3Blob) => {
					const mp3File = new File([mp3Blob], `recording_${timestamp}.mp3`, {
						type: "audio/mp3",
					});
					useAppStore.getState().updateRecording(id, { mp3File, isConverting: false });
				})
				.catch((error) => {
					console.error("WebM → MP3変換エラー:", error);
					// エラー時も変換中フラグをfalseにする
					useAppStore.getState().updateRecording(id, { isConverting: false });
				});
		} catch (error) {
			console.error("Error saving recording:", error);
		}
	}

	private cleanup(): void {
		this.mediaRecorder = null;
		this.chunks = [];
		this.startTime = null;
		this.analyser = null;
		if (this.audioContext) {
			this.audioContext.close().catch(() => {});
			this.audioContext = null;
		}
		audioStreamService.closeStream();
	}
}

// シングルトンインスタンスをエクスポート
export const recordingSession = new RecordingSession();
